import { useEffect } from "react";
import { ActionIcon, Alert, Badge, Box, Button, Group, ScrollArea, Stack, Text, Title, Tooltip, UnstyledButton } from "@mantine/core";
import { skipToken } from "@reduxjs/toolkit/query";
import { FaCheck, FaCircleInfo, FaXmark } from "react-icons/fa6";
import { MULTIPLACER_MODULES_BY_KEY } from "@multiconsult-group/multiplacer-core";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { setActiveLeftSideBarTabInRedux } from "../designSlice";
import { selectListedViewPresets, selectSelectedViewPresetIsDirty, selectViewPresetListCategory, selectViewPresetsQueryArgs } from "../viewPresetSelectors";
import { useApplyAndShareView } from "../SharedViews/useApplyAndShareView";
import { setViewSettingsPresetInRedux, setViewPresetListCategoryInRedux, type IViewPresetListCategory, type IViewPresetReference } from "../../ViewerRedux/viewerSlice";
import { useGetMultiplacerViewsQuery } from "../../../openapi";
import { ViewSettingsModal } from "./ViewSettingsModal";
import { SaveViewPresetButton } from "./SaveViewPresetButton";

const categories: { value: IViewPresetListCategory; label: string }[] = [
  { value: "builtIn", label: "Forhåndsdefinerte" },
  { value: "project", label: "Prosjekt" },
  { value: "personal", label: "Mine" },
];
const emptyMessages: Record<IViewPresetListCategory, string> = {
  builtIn: "Ingen forhåndsdefinerte visninger.",
  project: "Ingen prosjektvisninger er lagret i dette prosjektet.",
  personal: "Du har ingen personlige visninger i dette prosjektet.",
};

export const ViewsPanel = () => {
  const dispatch = useAppDispatch();
  useEffect(() => () => { dispatch(setViewSettingsPresetInRedux(null)); }, [dispatch]);
  const module = useAppSelector(state => state.design.activeModule);
  const moduleDefinition = module ? MULTIPLACER_MODULES_BY_KEY[module] : undefined;
  const defaultViewKey = moduleDefinition?.defaultViewKey;
  const selected = useAppSelector(state => state.viewer.selectedViewPreset);
  const isDirty = useAppSelector(selectSelectedViewPresetIsDirty);
  const { applyAndShare, isLoading, error } = useApplyAndShareView();
  const presets = useAppSelector(selectListedViewPresets);
  const category = useAppSelector(selectViewPresetListCategory);
  const queryArgs = useAppSelector(selectViewPresetsQueryArgs);
  const { isLoading: isListLoading, isError: isListError, refetch } = useGetMultiplacerViewsQuery(
    category === "builtIn" ? skipToken : queryArgs,
  );
  const showListLoading = category !== "builtIn" && isListLoading;
  const showListError = category !== "builtIn" && isListError;

  return (
    <Stack h="100%" gap="md" role="region" aria-labelledby="views-panel-title">
      <Group justify="space-between">
        <Title order={4} id="views-panel-title">Visninger</Title>
        <ActionIcon variant="subtle" color="gray" aria-label="Lukk visninger"
          onClick={() => dispatch(setActiveLeftSideBarTabInRedux(undefined))}>
          <FaXmark />
        </ActionIcon>
      </Group>
      <Box role="group" aria-label="Hvilke visninger skal vises">
        <Button.Group w="100%">
          {categories.map(option => (
            <Button key={option.value} size="xs" variant={category === option.value ? "default" : "outline"}
              aria-pressed={category === option.value}
              styles={{ root: { flex: "1 1 0", alignSelf: "stretch", minWidth: 0, height: "auto", minHeight: 34, paddingInline: 6, paddingBlock: 6 }, label: { whiteSpace: "normal" } }}
              onClick={() => dispatch(setViewPresetListCategoryInRedux(option.value))}>
              {option.label}
            </Button>
          ))}
        </Button.Group>
      </Box>
      {showListLoading && <Text size="sm" c="dimmed" role="status">Henter visninger…</Text>}
      {showListError && <Alert color="red" role="alert">
        Kunne ikke hente visningene.
        <Button mt="xs" size="xs" variant="neutral" onClick={() => void refetch()}>Prøv igjen</Button>
      </Alert>}
      {isLoading && <Text size="sm" c="dimmed" role="status">Oppretter delbar lenke…</Text>}
      {error && <Alert color="red" role="alert">{error}</Alert>}
      <ScrollArea style={{ flex: 1, minHeight: 0 }}>
        <Stack gap="xs">
          {presets.map(preset => {
            const reference: IViewPresetReference = preset.source === "builtIn"
              ? { source: "builtIn", key: preset.key } : { source: "user", id: preset._id };
            const active = reference.source === "builtIn"
              ? selected?.source === "builtIn" && selected.key === reference.key
              : selected?.source === "user" && selected.id === reference.id;
            return (
              <Group key={reference.source === "builtIn" ? `builtIn:${reference.key}` : `user:${reference.id}`} gap={0} wrap="nowrap"
                style={{
                  borderRadius: "var(--mantine-radius-sm)",
                  background: active ? "var(--mantine-primary-color-light)" : "var(--mantine-color-body)",
                  border: `1px solid ${active ? "var(--mantine-primary-color-filled)" : "var(--mantine-color-default-border)"}`,
                }}>
                <UnstyledButton p="sm" style={{ flex: 1, minWidth: 0 }} aria-pressed={active}
                  disabled={isLoading} onClick={() => void applyAndShare(preset.view, reference)}>
                <Group wrap="nowrap" justify="space-between">
                  <Box>
                    <Text size="sm" fw={600} c={active ? "var(--mantine-primary-color-filled)" : undefined}>{preset.name}</Text>
                    {active && isDirty && <Badge size="xs" color="orange" variant="light">Endret</Badge>}
                    {preset.source === "builtIn" && preset.key === defaultViewKey && (
                      <Badge mt={4} mb={4} size="xs" variant="light" color="blue"
                        styles={{ root: { height: "auto", maxWidth: "100%", paddingBlock: 3 }, label: { whiteSpace: "normal" } }}>
                        Standard · {moduleDefinition?.title}
                      </Badge>
                    )}
                    <Text size="xs" c="gray.7">{preset.description}</Text>
                  </Box>
                  {active && <Box c="var(--mantine-primary-color-filled)"><FaCheck aria-hidden /></Box>}
                </Group>
                </UnstyledButton>
                <Tooltip label="Vis innstillinger">
                  <ActionIcon mr="xs" variant="subtle" aria-label={`Vis innstillinger for ${preset.name}`}
                    onClick={() => dispatch(setViewSettingsPresetInRedux(reference))}><FaCircleInfo /></ActionIcon>
                </Tooltip>
              </Group>
            );
          })}
          {presets.length === 0 && !showListLoading && !showListError && <Text size="sm" c="dimmed">{emptyMessages[category]}</Text>}
        </Stack>
      </ScrollArea>
      <SaveViewPresetButton />
      <ViewSettingsModal />
    </Stack>
  );
};
